import React from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Description as ConsentIcon,
  Notifications as NoticeIcon,
  TrackChanges as RequestIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../config/routes";

interface ActivityItem {
  id: string;
  type: "consent" | "notice" | "request";
  title: string;
  description?: string;
  date: string;
  linkTo: string;
}

interface DashboardRecentActivityProps {
  recentConsents: Array<{
    consentHeaderId: number;
    collectionPointName: string;
    consentDate: string;
  }>;
  recentNotices: Array<{
    assignmentId: number;
    title: string;
    noticeType: string;
    assignedDate: string;
  }>;
  recentRequests: Array<{
    requestId: number;
    requestType: string;
    status: string;
    createdDate: string;
  }>;
  loading?: boolean;
}

const DashboardRecentActivity: React.FC<DashboardRecentActivityProps> = ({
  recentConsents,
  recentNotices,
  recentRequests,
  loading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  const groupByDate = (items: ActivityItem[]): Record<string, ActivityItem[]> => {
    const groups: Record<string, ActivityItem[]> = {
      Today: [],
      Yesterday: [],
      "This Week": [],
      Older: [],
    };

    items.forEach((item) => {
      const date = new Date(item.date);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        groups.Today.push(item);
      } else if (diffDays === 1) {
        groups.Yesterday.push(item);
      } else if (diffDays < 7) {
        groups["This Week"].push(item);
      } else {
        groups.Older.push(item);
      }
    });

    return groups;
  };

  const activityItems: ActivityItem[] = [
    ...recentConsents.slice(0, 7).map((consent) => ({
      id: `consent-${consent.consentHeaderId}`,
      type: "consent" as const,
      title: consent.collectionPointName,
      description: "Consent submitted",
      date: consent.consentDate,
      linkTo: `/consent/${consent.consentHeaderId}`,
    })),
    ...recentNotices.slice(0, 7).map((notice) => ({
      id: `notice-${notice.assignmentId}`,
      type: "notice" as const,
      title: notice.title,
      description: `${notice.noticeType} notice`,
      date: notice.assignedDate,
      linkTo: ROUTES.NOTICES,
    })),
    ...recentRequests.slice(0, 7).map((request) => ({
      id: `request-${request.requestId}`,
      type: "request" as const,
      title: `${request.requestType} Request #${request.requestId}`,
      description: `Status: ${request.status}`,
      date: request.createdDate,
      linkTo: `/requests/${request.requestId}`,
    })),
  ]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10); // Limit to 10 most recent

  const groupedItems = groupByDate(activityItems);

  if (loading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (activityItems.length === 0) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No recent activity to display.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getItemIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "consent":
        return <ConsentIcon fontSize="small" />;
      case "notice":
        return <NoticeIcon fontSize="small" />;
      case "request":
        return <RequestIcon fontSize="small" />;
    }
  };

  const getItemColor = (type: ActivityItem["type"]) => {
    switch (type) {
      case "consent":
        return theme.palette.success.main;
      case "notice":
        return theme.palette.info.main;
      case "request":
        return theme.palette.primary.main;
    }
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {activityItems.length} items
          </Typography>
        </Box>
        <List>
          {Object.entries(groupedItems).map(([groupName, items]) => {
            if (items.length === 0) return null;

            return (
              <React.Fragment key={groupName}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {groupName}
                  </Typography>
                </Box>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => navigate(item.linkTo)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Box sx={{ color: getItemColor(item.type), mr: 1.5 }}>
                          {getItemIcon(item.type)}
                        </Box>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              {item.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.description}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                {formatDate(item.date)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < items.length - 1 && (
                      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mx: 2 }} />
                    )}
                  </React.Fragment>
                ))}
                {groupName !== "Older" && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};

export default DashboardRecentActivity;

